import type { SupportedLanguage } from './index'

/**
 * Define localized routes here.
 * Each key maps to a route name, with translations for each language.
 *
 * Example:
 *   about: { it: 'chi-siamo', en: 'about-us' }
 *   → /it/chi-siamo, /en/about-us
 */
export const localizedRoutes: Record<string, Record<SupportedLanguage, string>> = {
  home: {
    it: '',
    en: '',
    de: '',
    es: '',
    fr: '',
    pl: '',
    pt: '',
  },
  about: {
    it: 'azienda',
    en: 'about',
    de: 'unternehmen',
    es: 'empresa',
    fr: 'entreprise',
    pl: 'firma',
    pt: 'empresa',
  },
  method: {
    it: 'metodo',
    en: 'method',
    de: 'methode',
    es: 'metodo',
    fr: 'methode',
    pl: 'metoda',
    pt: 'metodo',
  },
  products: {
    it: 'prodotti',
    en: 'products',
    de: 'produkte',
    es: 'productos',
    fr: 'produits',
    pl: 'produkty',
    pt: 'produtos',
  },
  'product-outsourcing': {
    it: 'prodotti/produzione-estera',
    en: 'products/outsourcing',
    de: 'produkte/outsourcing',
    es: 'productos/outsourcing',
    fr: 'produits/outsourcing',
    pl: 'produkty/outsourcing',
    pt: 'produtos/outsourcing',
  },
  'product-machining': {
    it: 'prodotti/lavorazioni-meccaniche',
    en: 'products/machining',
    de: 'produkte/bearbeitung',
    es: 'productos/mecanizado',
    fr: 'produits/usinage',
    pl: 'produkty/obrobka',
    pt: 'produtos/usinagem',
  },
  'product-transmission': {
    it: 'prodotti/organi-di-trasmissione',
    en: 'products/transmission',
    de: 'produkte/getriebe',
    es: 'productos/transmision',
    fr: 'produits/transmission',
    pl: 'produkty/przekladnie',
    pt: 'produtos/transmissao',
  },
  'product-plastics': {
    it: 'prodotti/materie-plastiche',
    en: 'products/plastics',
    de: 'produkte/kunststoffe',
    es: 'productos/plasticos',
    fr: 'produits/plastiques',
    pl: 'produkty/tworzywa-sztuczne',
    pt: 'produtos/plasticos',
  },
  'product-assembly': {
    it: 'assemblaggi',
    en: 'assembly',
    de: 'montage',
    es: 'ensamblaje',
    fr: 'assemblage',
    pl: 'montaz',
    pt: 'montagem',
  },
  careers: {
    it: 'lavora-con-noi',
    en: 'careers',
    de: 'karriere',
    es: 'empleo',
    fr: 'carrieres',
    pl: 'kariera',
    pt: 'carreiras',
  },
  skills: {
    it: 'skills',
    en: 'skills',
    de: 'skills',
    es: 'skills',
    fr: 'skills',
    pl: 'skills',
    pt: 'skills',
  },
  contacts: {
    it: 'contatti',
    en: 'contacts',
    de: 'kontakt',
    es: 'contacto',
    fr: 'contact',
    pl: 'kontakt',
    pt: 'contato',
  },
  privacy: {
    it: 'privacy',
    en: 'privacy',
    de: 'privacy',
    es: 'privacy',
    fr: 'privacy',
    pl: 'privacy',
    pt: 'privacy',
  },
  cookies: {
    it: 'cookie-policy',
    en: 'cookie-policy',
    de: 'cookie-policy',
    es: 'cookie-policy',
    fr: 'cookie-policy',
    pl: 'cookie-policy',
    pt: 'cookie-policy',
  },
}

export type RouteKey = keyof typeof localizedRoutes

/**
 * Get the localized path for a route
 */
export function getLocalizedPath(
  routeKey: RouteKey,
  lang: SupportedLanguage
): string {
  const path = localizedRoutes[routeKey][lang]
  return path ? `/${lang}/${path}` : `/${lang}`
}

/**
 * Find route key from a path segment
 */
export function findRouteKeyFromPath(
  pathSegment: string,
  lang: SupportedLanguage
): RouteKey | null {
  for (const [key, routes] of Object.entries(localizedRoutes)) {
    if (routes[lang] === pathSegment) {
      return key as RouteKey
    }
  }
  return null
}
